// NA1-nav-loading — kit skeleton templates, shaped per awaited page type
// (doctrine: never a generic bar-stack where the shape is known). Used as
// route-level Suspense fallbacks (RouteFallback.tsx) and reusable by pages.
import { Skeleton } from '@/components/ui/skeleton';

/** Generic 3-bar page skeleton — ONLY for surfaces whose shape is unknowable
 *  (v1-fallback routes). Shape-known pages use the templates below. */
export const GenericPageSkeleton = () => (
  <div className="flex flex-col gap-4" data-testid="v4-skeleton-generic">
    <Skeleton className="h-8 w-64" />
    <Skeleton className="h-4 w-96" />
    <Skeleton className="h-64 w-full" />
  </div>
);

/** List page: title + toolbar (search start, filters end) + APCollection's
 *  row shape (avatar circle + two text lines). */
export const ListPageSkeleton = ({ rows = 8 }: { rows?: number }) => (
  <div className="flex flex-col gap-4" data-testid="v4-skeleton-list">
    <Skeleton className="h-8 w-64" />
    <div className="flex items-center gap-2">
      <Skeleton className="h-9 w-28" />
      <Skeleton className="h-9 w-64" />
      <Skeleton className="ms-auto h-9 w-24" />
    </div>
    <div className="rounded-lg border bg-card">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="flex items-center gap-3 px-4 py-3">
          <Skeleton className="size-9 rounded-full" />
          <div className="flex flex-1 flex-col gap-1.5">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-3.5 w-72" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

/** Form page: header + APFormShell's floating card with field rows. */
export const FormPageSkeleton = () => (
  <div className="flex flex-col gap-6" data-testid="v4-skeleton-form">
    <div className="flex items-center gap-2">
      <Skeleton className="size-7" />
      <Skeleton className="h-8 w-64" />
    </div>
    <div className="rounded-lg border bg-card p-6">
      <div className="grid gap-3">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-2/3" />
      </div>
    </div>
  </div>
);

/** Show page: APShowPage's loading shape (back + title, subtitle, body). */
export const ShowPageSkeleton = () => (
  <div className="flex flex-col gap-6" data-testid="v4-skeleton-show">
    <div className="flex items-center gap-2">
      <Skeleton className="size-7" />
      <Skeleton className="h-8 w-64" />
    </div>
    <Skeleton className="h-4 w-96" />
    <Skeleton className="h-64 w-full" />
  </div>
);

/** Settings shell: icon + h1 row, tab-strip pills, panel block. */
export const SettingsShellSkeleton = () => (
  <div className="flex flex-col gap-4" data-testid="v4-skeleton-settings-shell">
    <div className="flex items-center gap-2">
      <Skeleton className="size-6 rounded-full" />
      <Skeleton className="h-8 w-64" />
    </div>
    <div className="flex flex-wrap items-center gap-1">
      {Array.from({ length: 6 }).map((_, index) => (
        <Skeleton key={index} className="h-8 w-24" />
      ))}
    </div>
    <Skeleton className="h-64 w-full" />
  </div>
);

/** Manage shell (full-screen, outside AppShell): exam header bar + tab strip
 *  + tab body block. */
export const ManageShellSkeleton = ({
  testId = 'v4-skeleton-manage-shell',
}: {
  testId?: string;
}) => (
  <div
    className="flex min-h-screen flex-col gap-4 bg-ap-canvas p-6 text-foreground"
    data-testid={testId}
  >
    <div className="flex items-center gap-3">
      <Skeleton className="size-8" />
      <Skeleton className="h-8 w-64" />
      <Skeleton className="ms-auto h-9 w-32" />
    </div>
    <Skeleton className="h-10 w-96" />
    <Skeleton className="h-64 w-full" />
  </div>
);

/** Take shell (full-screen, outside AppShell): exam header bar + pinned rail
 *  column + question cards. */
export const TakeShellSkeleton = ({ testId = 'v4-skeleton-take-shell' }: { testId?: string }) => (
  <div
    className="flex min-h-screen flex-col gap-4 bg-ap-canvas p-6 text-foreground"
    data-testid={testId}
  >
    <Skeleton className="h-14 w-full" />
    <div className="flex flex-1 gap-4">
      <div className="flex w-64 shrink-0 flex-col gap-2 max-md:hidden">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} className="h-8 w-full" />
        ))}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-4">
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    </div>
  </div>
);

/** Grade modal (CR1 3-panel mirror): left student rail (switcher card +
 *  action stubs + grading field rows) · question strip · body answer cards.
 *  Pane dims track GradeModal's real layout (rail md:w-88, strip row, p-4
 *  body) so content replaces the skeleton without shift. */
export const GradeModalSkeleton = ({ testId = 'v4-skeleton-grade-modal' }: { testId?: string }) => (
  <div className="flex min-h-0 flex-1 flex-col md:flex-row" data-testid={testId}>
    <div className="flex shrink-0 flex-col gap-3 border-b border-border bg-ap-muted-a20 p-3 md:border-b-0 md:border-e md:w-88">
      <Skeleton className="h-20 w-full" />
      <div className="flex items-center gap-2">
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-5 w-16" />
      </div>
      <div className="flex items-center gap-1.5">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-8 w-20" />
      </div>
      <div className="flex flex-col gap-2 border-t border-border pt-3">
        <Skeleton className="h-9 w-full" />
        <Skeleton className="h-9 w-2/3" />
      </div>
    </div>
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2 pe-14">
        {Array.from({ length: 6 }).map((_, index) => (
          <Skeleton key={index} className="h-8 w-10" />
        ))}
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-4 bg-ap-canvas p-4 sm:p-6">
        {Array.from({ length: 2 }).map((_, index) => (
          <div key={index} className="overflow-hidden rounded-lg border border-border bg-card">
            <div className="flex min-h-9 items-center gap-2 px-4 py-1.5">
              <Skeleton className="h-4 w-12" />
              <Skeleton className="h-4 w-16" />
            </div>
            <div className="border-t border-border px-4 py-3">
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/** Dashboard: KPI tile row + chart block + table block. */
export const DashboardSkeleton = () => (
  <div className="flex flex-col gap-4" data-testid="v4-skeleton-dashboard">
    <Skeleton className="h-8 w-64" />
    <div className="flex flex-wrap gap-4">
      {Array.from({ length: 4 }).map((_, index) => (
        <Skeleton key={index} className="h-24 min-w-40 flex-1" />
      ))}
    </div>
    <Skeleton className="h-64 w-full" />
    <Skeleton className="h-48 w-full" />
  </div>
);
